import React, { useState, useEffect } from "react";
import { Box, Grid, Space } from "@mantine/core";
import { MantineProvider } from "@mantine/core";
import { useNavigate } from "react-router-dom";

import SearchBar from "../components/searchBar";
import PrecedentComponent from "../components/precedent";

import OrderViewPatientComponent from "../components/OrderViewPatientComponent";
import OrderViewStepperComponent from "../components/OrderViewStepperComponent";
import OrderViewSelectedTeeth from "../components/OrderViewSelectedTeeth";
import OrderViewOrderDetail from "../components/OrderViewOrderDetail";
import HeaderOrderView from "../components/HeaderOrderView";
import LabsBreadcrumbs from "../components/labsBreadcrumbs";
import { Chat } from "../stories/Chat/Chat";
import {
  Message,
  Order,
  Product,
  User,
  File as SharedFile,
} from "../types/interfaces";
import "./orderViewPage.css";

interface OrderViewPageProps {
  orderComments: any;
  order: Order;
  user: User;
  refetch: () => void;
  setMessage: (message: string) => void;
  messages: Message[];
  sendMessage: () => void;
  filesToSend: File[];
  setFilesToSend: React.Dispatch<React.SetStateAction<File[]>>;
  loading: boolean;
  hasMore: boolean;
  fetchMoreMessages: () => void;
  scrollBottom: boolean;
  fileWithPath: SharedFile[];
  refetchFiles: () => void;
  setPictureToMarkAsRead: (picture: string) => void;
  onSearch?: (searchText: string) => void;
  onBack?: () => void;
}

const OrderViewPage = ({
  orderComments,
  order,
  user,
  refetch,
  setMessage,
  messages,
  sendMessage,
  filesToSend,
  setFilesToSend,
  loading,
  hasMore,
  fetchMoreMessages,
  scrollBottom,
  fileWithPath,
  setPictureToMarkAsRead,
  onSearch,
  onBack,
}: OrderViewPageProps) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const navigate = useNavigate();

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchText);
    } else {
      navigate("/home");
    }
  };

  useEffect(() => {
    if ((order?.products ?? []).length > 0) {
      setSelectedProduct(order?.products[0] || null);
    }
  }, [order?.products]);

  if (!order) {
    return null;
  }

  return (
    <MantineProvider>
      <div
        id="back-and-search-bar"
        style={{ paddingBottom: "1rem", display: "flex" }}
      >
        <PrecedentComponent onBack={onBack} />
        <SearchBar
          searchText={searchText}
          setSearchText={value => setSearchText(value)}
          onConfirm={handleSearch}
        />
      </div>
      <div id="breadcrumbs">
        <LabsBreadcrumbs
          orderReference={order?.orderReference}
          orderId={order?.id}
          user={user}
          searchText=""
        />
      </div>
      <Box
        p="lg"
        style={() => ({
          display: "flex",
          flexDirection: "column",
          height: "100%",
        })}
      >
        <div id="header-order-view">
          <HeaderOrderView order={order} user={user} refetch={refetch} />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            width: "100%",
            gap: "20px",
          }}
        >
          <div style={{ gridColumn: "1 / 3" }}>
            <Grid
              id="order-view-patient-and-stepper-grid"
              gap="md"
              style={{
                flexGrow: 1,
                minHeight: "fit-content",
                maxHeight: "30%",
              }}
            >
              <OrderViewPatientComponent
                id="order-view-patient-component"
                order={order}
                user={user}
                refetch={refetch}
              />
              <OrderViewStepperComponent
                id="order-view-stepper-component"
                order={order}
                user={user}
              />
            </Grid>
            <Space h="md" />
            <Grid gap="md" style={{ flexGrow: 1 }}>
              <OrderViewSelectedTeeth
                id="order-view-selected-teeth"
                order={order}
                selectedProduct={selectedProduct}
              />
              <OrderViewOrderDetail
                id="order-view-order-detail"
                setPictureToMarkAsRead={setPictureToMarkAsRead}
                initialOrderComments={orderComments}
                fileWithPath={fileWithPath}
                order={order}
                selectedProduct={selectedProduct}
                setSelectedProduct={setSelectedProduct}
                user={user}
                refetch={refetch}
              />
            </Grid>
          </div>
          <div style={{ gridColumn: "3 / 3", display: "grid" }}>
            <Chat
              messages={messages}
              currentUserGroup={user?.userGroupId}
              setMessageToSend={setMessage}
              send={sendMessage}
              filesToSend={filesToSend}
              setFilesToSend={setFilesToSend}
              loading={loading}
              hasMore={hasMore}
              fetchMoreMessages={fetchMoreMessages}
              scrollBottom={scrollBottom}
              user={user}
            />
          </div>
        </div>
      </Box>
    </MantineProvider>
  );
};

export default OrderViewPage;
